/**
 * The header account icon's popup (snippets/header-actions.liquid): Profile,
 * Wish List and Create a Return.
 *
 * - Hover opens it after the header menus' hover delay (--animation-delay on
 *   .forcast-header__nav), so sweeping the pointer along the icons doesn't flash
 *   it; leaving closes it after the same delay, which forgives the trip from the
 *   icon down into the panel.
 * - Keyboard focus opens it at once, Escape closes it and puts focus back on the
 *   icon, and tabbing out closes it.
 * - A touch screen has no hover, so the first tap on the icon opens the popup
 *   instead of following the link; a second tap follows it, and a tap anywhere
 *   else closes it.
 * - Choosing an item closes it.
 *
 * The icon stays a link to the account, so a mouse click, a keyboard Enter, or a
 * visit with this script missing still goes straight there.
 */

/** The theme-wide default, if the header's own override can't be read. */
const FALLBACK_DELAY = 300;

class ForcastAccountMenu extends HTMLElement {
  #openTimer = 0;
  #closeTimer = 0;
  /** How the last press on the icon was made, and whether the popup was open then: a first tap from a second. */
  #press = /** @type {{ type: string, wasOpen: boolean } | null} */ (null);
  /** Set while Escape hands focus back to the icon, so that focus doesn't reopen it. */
  #refocusing = false;

  connectedCallback() {
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#setOpen(false);
  }

  get #link() {
    return this.querySelector('.account-button__link');
  }

  get #isOpen() {
    return this.classList.contains('is-open');
  }

  /** The header menus' hover delay, read where they read it, so the two can't drift. */
  get #delay() {
    const source = document.querySelector('.forcast-header__nav') ?? this;
    const raw = getComputedStyle(source).getPropertyValue('--animation-delay').trim();
    const ms = raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
    return Number.isFinite(ms) ? ms : FALLBACK_DELAY;
  }

  /** @param {boolean} open */
  #setOpen(open) {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.classList.toggle('is-open', open);
    this.#link?.setAttribute('aria-expanded', String(open));
    if (open) document.addEventListener('pointerdown', this.#onOutsidePress, true);
    else document.removeEventListener('pointerdown', this.#onOutsidePress, true);
  }

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#closeTimer);
    if (this.#isOpen) return;
    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => this.#setOpen(true), this.#delay);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#openTimer);
    if (!this.#isOpen) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#setOpen(false), this.#delay);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const link = this.#link;
    if (!link || !(event.target instanceof Node) || !link.contains(event.target)) return;
    this.#press = { type: event.pointerType, wasOpen: this.#isOpen };
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const link = this.#link;
    if (link?.contains(target)) {
      const press = this.#press;
      this.#press = null;
      // A finger or a pen had no hover to open the popup with: the first tap
      // opens it, the next one follows the link.
      if (press && press.type !== 'mouse' && !press.wasOpen) {
        event.preventDefault();
        this.#setOpen(true);
      }
      return;
    }

    // An item was chosen: its own action (a link, or the drawer the Wish List
    // button opens through its on:click) has already run.
    if (target.closest('.forcast-account-menu__item')) this.#setOpen(false);
  };

  #onFocusIn = () => {
    if (this.#refocusing) {
      this.#refocusing = false;
      return;
    }
    // A tap's focus arrives before its click, which decides for itself.
    if (this.#press && this.#press.type !== 'mouse') return;
    this.#setOpen(true);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    // A mouse user still over the icon keeps it until the pointer leaves.
    if (this.matches(':hover')) return;
    this.#setOpen(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#isOpen) return;
    this.#setOpen(false);
    const link = this.#link;
    // Focusing the icon again would reopen the popup, unless it already had
    // focus, in which case no focus event comes at all.
    if (link && document.activeElement !== link) {
      this.#refocusing = true;
      link.focus();
    }
  };

  /** @param {PointerEvent} event */
  #onOutsidePress = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#setOpen(false);
  };
}

if (!customElements.get('forcast-account-menu')) {
  customElements.define('forcast-account-menu', ForcastAccountMenu);
}
